import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  FileDown,
  FileText,
  KeyRound,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Slash,
  TrendingUp,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import StatCard from "@/components/ui/StatCard";
import StatusBadge, { STATUS_META, type MandateStatus } from "@/components/ui/StatusBadge";
import Textarea from "@/components/ui/Textarea";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { KycStatus } from "@/types/kyc";
import KycStatusBadge from "@/pages/admin/kyc/components/KycStatusBadge";
import { MONTHS, formatDate, formatINR, relativeTime } from "@/pages/admin/data";
import DonutChart, { type DonutSegment } from "@/pages/retailer/dashboard/components/DonutChart";
import LineChart, { type SeriesPoint } from "@/pages/retailer/dashboard/components/LineChart";
import RetailerFormModal from "./components/RetailerFormModal";
import ResetPasswordModal from "./components/ResetPasswordModal";
import DeactivateModal from "./components/DeactivateModal";
import { formatJoined, initials } from "./data";
import type { Retailer } from "./types";
import { adminService, kycService, mandateService, walletService } from "@/services/apiServices";

const STATUS_ORDER: MandateStatus[] = [
  "ACTIVE",
  "PENDING_AUTH",
  "CREATED",
  "COMPLETED",
  "TERMINATED",
  "CANCELLED",
  "PAUSED",
  "HALTED",
];

const DONUT_COLORS: Record<MandateStatus, string> = {
  ACTIVE: "#059669",
  PENDING_AUTH: "#D97706",
  CREATED: "#0284C7",
  COMPLETED: "#1E40AF",
  TERMINATED: "#DC2626",
  CANCELLED: "#475569",
  PAUSED: "#D97706",
  HALTED: "#DC2626",
};

interface RetailerMandate {
  id: string;
  refNo: string;
  customerName: string;
  customerMobile: string;
  financedAmount: number;
  totalPayable: number;
  tenureMonths: number;
  status: MandateStatus;
  createdAt: string;
}

interface KycRecordView {
  status: string;
  businessType?: string;
  panNumber?: string;
  submittedAt?: string;
}

interface WalletView {
  totalBalance: number;
  status: string;
  lifetimeSpent: number;
  lastRechargeAt: string | null;
}

interface Note {
  text: string;
  by: string;
  ts: string;
}

export default function AdminRetailerDetail() {
  return (
    <ToastProvider>
      <RetailerDetailInner />
    </ToastProvider>
  );
}

function RetailerDetailInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [retailer, setRetailer] = useState<Retailer | null>(null);
  const [myMandates, setMyMandates] = useState<RetailerMandate[]>([]);
  const [kyc, setKyc] = useState<KycRecordView | null>(null);
  const [wallet, setWallet] = useState<WalletView | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<Note[]>([]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    Promise.all([
      adminService.getRetailerById(id),
      mandateService.getMandates({ retailerId: id, limit: 1000 }),
      kycService.getStatus(id),
      walletService.getBalance(id),
      walletService.getTransactions(id),
    ])
      .then(([retailerRes, mandatesRes, kycRes, walletRes, walletTxRes]) => {
        if (cancelled) return;
        setRetailer(retailerRes?.data?.retailer ?? null);
        setMyMandates(mandatesRes?.data?.mandates ?? []);

        const kycData = kycRes?.data;
        setKyc(
          kycData
            ? {
                status: kycData.status,
                businessType: kycData.record?.businessType,
                panNumber: kycData.record?.panNumber,
                submittedAt: kycData.record?.submittedAt,
              }
            : null
        );

        const balanceData = walletRes?.data;
        const transactions: { type: string; amount: number; timestamp: string }[] =
          walletTxRes?.data?.transactions ?? [];
        const lifetimeSpent = transactions
          .filter((t) => t.type === "DEBIT")
          .reduce((acc, t) => acc + Number(t.amount || 0), 0);
        const lastRecharge = transactions.find((t) => t.type === "CREDIT");
        setWallet(
          balanceData
            ? {
                totalBalance: Number(balanceData.totalBalance || 0),
                status: balanceData.status,
                lifetimeSpent,
                lastRechargeAt: lastRecharge?.timestamp ?? null,
              }
            : null
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setNotFound(true);
        toast.error(err?.message || "Failed to load retailer details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, toast]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = () => setMenuOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [menuOpen]);

  const sortedMandates = useMemo(
    () => [...myMandates].sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1)),
    [myMandates]
  );

  const stats = useMemo(() => {
    if (myMandates && myMandates.length > 0) {
      const totalMandates = myMandates.length;
      const activeMandates = myMandates.filter(
        (m) => String(m.status || "").trim().toUpperCase() === "ACTIVE"
      ).length;
      const pendingAuth = myMandates.filter((m) => {
        const s = String(m.status || "").trim().toUpperCase();
        return s === "PENDING_AUTH" || s === "CREATED";
      }).length;
      const successful = myMandates.filter((m) => {
        const s = String(m.status || "").trim().toUpperCase();
        return s === "ACTIVE" || s === "COMPLETED";
      }).length;
      const successRate = totalMandates > 0 ? Math.round((successful / totalMandates) * 100) : 0;
      return {
        totalMandates,
        activeMandates,
        pendingAuth,
        successRate,
      };
    }
    return {
      totalMandates: retailer?.totalMandates ?? 0,
      activeMandates: retailer?.activeMandates ?? 0,
      pendingAuth: retailer?.pendingAuth ?? 0,
      successRate: retailer?.successRate ?? 0,
    };
  }, [myMandates, retailer]);

  const segments: DonutSegment[] = useMemo(() => {
    const counts = new Map<MandateStatus, number>();
    sortedMandates.forEach((m) => {
      const statusUpper = (m.status ? String(m.status).trim().toUpperCase() : "CREATED") as MandateStatus;
      counts.set(statusUpper, (counts.get(statusUpper) ?? 0) + 1);
    });
    return STATUS_ORDER.filter((s) => (counts.get(s) ?? 0) > 0).map((s) => ({
      label: s,
      value: counts.get(s)!,
      color: DONUT_COLORS[s] || "#0284C7",
    }));
  }, [sortedMandates]);

  const activityData: SeriesPoint[] = useMemo(() => {
    const days: SeriesPoint[] = [];
    const countByDay = new Map<string, number>();
    sortedMandates.forEach((m) => {
      const d = new Date(m.createdAt);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      countByDay.set(key, (countByDay.get(key) ?? 0) + 1);
    });
    const today = new Date();
    for (let i = 29; i >= 0; i -= 1) {
      const dt = new Date(today);
      dt.setDate(today.getDate() - i);
      const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
      days.push({ label: `${dt.getDate()} ${MONTHS[dt.getMonth()]}`, value: countByDay.get(key) ?? 0 });
    }
    return days;
  }, [sortedMandates]);

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4">
          <div className="skeleton-shimmer h-8 w-40 rounded-md" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-[10px] border border-line bg-card p-5 shadow-card">
                <div className="skeleton-shimmer h-3 w-28 rounded-md" />
                <div className="skeleton-shimmer mt-3 h-8 w-20 rounded-md" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
            <div className="skeleton-shimmer h-[360px] rounded-[10px]" />
            <div className="skeleton-shimmer h-[500px] rounded-[10px]" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !retailer) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-6">
        <EmptyState
          title="Retailer not found"
          description="This retailer could not be found."
          actionLabel="Back to Retailers"
          onAction={() => navigate("/admin/retailers")}
        />
      </div>
    );
  }

  const hasMandates = sortedMandates.length > 0;
  const recent = sortedMandates.slice(0, 8);

  async function handleSave(data: Partial<Retailer> & { password?: string }, targetId?: string) {
    const useId = targetId ?? retailer!.id;
    const res = await adminService.updateRetailer(useId, data);
    setRetailer(res?.data?.retailer ?? null);
    toast.success(res?.message || "Retailer updated successfully");
  }

  async function handleDeactivate(targetId: string, reason: string, remarks: string) {
    const res = await adminService.toggleRetailerStatus(targetId, { isActive: false, reason, remarks });
    setRetailer(res?.data?.retailer ?? null);
    toast.success(res?.message || "Retailer deactivated");
  }

  async function handleActivate() {
    if (!retailer) return;
    try {
      const res = await adminService.toggleRetailerStatus(retailer.id, { isActive: true });
      setRetailer(res?.data?.retailer ?? null);
      toast.success(res?.message || "Retailer activated");
    } catch (err: any) {
      toast.error(err?.message || "Failed to activate retailer");
    }
  }

  async function handleResetPassword(targetId: string, password: string, notify: boolean) {
    const res = await adminService.resetRetailerPassword(targetId, { password, notify });
    toast.success(res?.message || "Password reset successfully");
  }

  function saveNote() {
    if (!note.trim()) return;
    const newNote: Note = {
      text: note.trim(),
      by: "Admin",
      ts: formatDate(new Date().toISOString()),
    };
    setNotes((prev) => [newNote, ...prev]);
    setNote("");
    toast.success("Note saved");
  }

  function renderDeviceId(devId?: string) {
    if (!devId) return <span className="text-ink-muted">—</span>;
    return (
      <div className="flex items-center gap-1.5 justify-end">
        <span className="font-mono text-xs text-slate-700 bg-slate-100 px-2 py-0.5 rounded max-w-[210px] truncate" title={devId}>
          {devId}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(devId);
            toast.success("Device ID copied to clipboard");
          }}
          className="p-1 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100 transition-colors cursor-pointer"
          title="Copy Device ID"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  function exportRetailerReport() {
    if (!retailer) {
      toast.error("Retailer data not loaded");
      return;
    }
    setMenuOpen(false);

    try {
      const summaryHeaders = [
        "Retailer ID",
        "Owner Name",
        "Shop/Business Name",
        "Business Type",
        "Mobile Number",
        "Email Address",
        "City",
        "State",
        "Business Address",
        "GST Number",
        "KYC Status",
        "Wallet Balance (INR)",
        "Account Status",
        "Total Mandates",
        "Active Mandates",
        "Pending Auth",
        "Failed / Rejected",
        "Success Rate",
        "Joined On",
        "Last Login",
      ];
      const summaryValues = [
        retailer.id,
        retailer.name,
        retailer.shopName,
        retailer.businessType || kyc?.businessType || "Proprietorship",
        retailer.mobile,
        retailer.email,
        retailer.city,
        retailer.state,
        retailer.fullAddress || "",
        retailer.gst || "",
        retailer.kycStatus || "",
        retailer.walletBalance ?? 0,
        retailer.isActive ? "Active" : "Inactive",
        stats.totalMandates,
        stats.activeMandates,
        stats.pendingAuth,
        stats.failedRejected ?? (myMandates || []).filter((m) => {
          const s = String(m.status || "").trim().toUpperCase();
          return s === "FAILED" || s === "REJECTED" || s === "DECLINED";
        }).length,
        `${stats.successRate}%`,
        formatJoined(retailer.joinedAt),
        retailer.lastLoginAt ? formatDate(retailer.lastLoginAt) : "Never",
      ];

      const mandateHeaders = [
        "Reference No",
        "Customer Name",
        "Customer Mobile",
        "Amount (INR)",
        "Tenure (Months)",
        "EMI Amount (INR)",
        "Status",
        "Created At",
      ];
      const mandateRows = (myMandates || []).map((m) => [
        m.refNo,
        m.customerName,
        m.customerMobile,
        m.productPrice || m.totalPayable,
        m.tenureMonths,
        m.emiAmount,
        m.status,
        m.createdAt,
      ]);

      const escapeVal = (v: unknown) => {
        if (v === null || v === undefined) return '""';
        const str = String(v).replace(/"/g, '""');
        return `"${str}"`;
      };

      const lines: string[] = [
        `RETAILER SUMMARY - ${retailer.name} (${retailer.id})`,
        "",
        summaryHeaders.map(escapeVal).join(","),
        summaryValues.map(escapeVal).join(","),
        "",
        `MANDATES LIST (${(myMandates || []).length})`,
        mandateHeaders.map(escapeVal).join(","),
        ...mandateRows.map((r) => r.map(escapeVal).join(",")),
      ];

      const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `retailer-${retailer.id}-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Retailer report exported successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to export retailer report");
    }
  }

  const accountRows: { label: string; value: ReactNode }[] = [
    { label: "Owner Name", value: retailer.name },
    { label: "Shop/Business Name", value: retailer.shopName || "—" },
    { label: "Business Type", value: retailer.businessType || kyc?.businessType || "Proprietorship" },
    { label: "Retailer Code", value: retailer.userCode || retailer.id },
    { label: "User ID", value: retailer.id },
    { label: "Onboarded By", value: (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
        {retailer.createdByName || "Super Admin"}
      </span>
    )},
    { label: "Device ID", value: renderDeviceId(retailer.deviceId) },
    { label: "Mobile Number", value: `+91 ${retailer.mobile}` },
    { label: "Email Address", value: retailer.email },
    { label: "City", value: retailer.city || "—" },
    { label: "State", value: retailer.state || "—" },
    { label: "Full Address", value: retailer.fullAddress || "—" },
    { label: "GST Number", value: retailer.gst || "—" },
    { label: "Joined On", value: formatJoined(retailer.joinedAt) },
    { label: "Last Login", value: retailer.lastLoginAt ? relativeTime(retailer.lastLoginAt) : "—" },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6">
      <button
        type="button"
        onClick={() => navigate("/admin/retailers")}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink-secondary hover:text-ink cursor-pointer"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Retailers
      </button>

      <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-50 text-lg font-semibold text-primary-500">
            {initials(retailer.name)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="truncate text-2xl font-semibold text-ink">{retailer.shopName}</h1>
              {retailer.isActive ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-success-bg px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-badgeSlate-bg px-2.5 py-1 text-xs font-medium text-slate-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-badgeSlate" />
                  Inactive
                </span>
              )}
            </div>
            <p className="mt-1 truncate text-[13px] text-ink-secondary">
              {retailer.name} · {retailer.userCode && !retailer.userCode.includes("-") ? retailer.userCode : retailer.name} · Joined {formatJoined(retailer.joinedAt)}
            </p>
          </div>
        </div>

        <div className="relative flex flex-wrap items-center gap-2.5">
          <Button variant="secondary" leftIcon={<Pencil className="h-4 w-4" />} onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button variant="secondary" leftIcon={<KeyRound className="h-4 w-4" />} onClick={() => setResetOpen(true)}>
            Reset Password
          </Button>
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-ink-secondary hover:bg-slate-50 hover:text-ink cursor-pointer"
              aria-label="More actions"
            >
              <MoreHorizontal className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-20 mt-1 w-52 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                <MenuAction
                  icon={<ListChecks className="h-4 w-4 text-ink-muted" />}
                  onClick={() => navigate(`/admin/mandates?retailer=${retailer.id}`)}
                >
                  View Mandates
                </MenuAction>
                <MenuAction
                  icon={<Clock className="h-4 w-4 text-ink-muted" />}
                  onClick={() => navigate(`/admin/logs?retailer=${retailer.id}`)}
                >
                  View Activity Logs
                </MenuAction>
                <MenuAction
                  icon={<FileDown className="h-4 w-4 text-ink-muted" />}
                  onClick={exportRetailerReport}
                >
                  Export Retailer Report
                </MenuAction>
                {retailer.isActive && (
                  <MenuAction
                    icon={<Slash className="h-4 w-4 text-danger" />}
                    danger
                    onClick={() => setDeactivateOpen(true)}
                  >
                    Deactivate
                  </MenuAction>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {!retailer.isActive && (
        <div className="mt-6 flex flex-col gap-4 rounded-[10px] border border-[#E2E8F0] bg-[#F1F5F9] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500">
              <Slash className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">This retailer account is inactive</p>
              <p className="mt-0.5 text-[13px] text-ink-secondary">
                {retailer.deactivatedAt ? `Deactivated on ${formatDate(retailer.deactivatedAt)}` : "Deactivated"} · Reason:{" "}
                {retailer.deactivationReason || "Not specified"}
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={handleActivate}>
            Activate Account
          </Button>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total Mandates"
            value={`${stats.totalMandates}`}
            icon={FileText}
            iconBg="bg-[#EFF6FF]"
            iconColor="text-[#1E40AF]"
            onClick={() => navigate(`/admin/mandates?retailer=${retailer.id}`)}
          />
          <StatCard
            label="Active Mandates"
            value={`${stats.activeMandates}`}
            icon={CheckCircle2}
            iconBg="bg-[#ECFDF5]"
            iconColor="text-[#059669]"
            onClick={() => navigate(`/admin/mandates?retailer=${retailer.id}`)}
          />
          <StatCard
            label="Pending Auth"
            value={`${stats.pendingAuth}`}
            icon={Clock}
            iconBg="bg-[#FFFBEB]"
            iconColor="text-[#D97706]"
            onClick={() => navigate(`/admin/mandates?retailer=${retailer.id}`)}
          />
          <StatCard
            label="Success Rate"
            value={`${stats.successRate}%`}
            icon={TrendingUp}
            iconBg="bg-[#F0F9FF]"
            iconColor="text-[#0284C7]"
            onClick={() => navigate(`/admin/reports`)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
          <div className="flex flex-col gap-4">
            {hasMandates ? (
              <>
                <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
                  <h3 className="text-lg font-semibold text-ink">Mandate Activity</h3>
                  <div className="mt-4">
                    <LineChart data={activityData} height={240} />
                  </div>
                </div>

                <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-ink">Recent Mandates</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/mandates?retailer=${retailer.id}`)}
                      rightIcon={<ArrowRight className="h-4 w-4" />}
                    >
                      View All
                    </Button>
                  </div>
                  <div className="mt-3 overflow-x-auto max-h-[380px] overflow-y-auto rounded-[10px] border border-line">
                    <table className="w-full min-w-[560px] text-left border-collapse">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b border-line">
                          <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                            Ref No
                          </th>
                          <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                            Customer
                          </th>
                          <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                            Amount
                          </th>
                          <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                            Status
                          </th>
                          <th className="sticky top-0 z-10 bg-[#f8fafc] border-b border-line shadow-[0_1px_0_0_#e2e8f0] px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {recent.map((m) => (
                          <tr
                            key={m.id}
                            className="h-14 border-b border-line last:border-0 transition-colors hover:bg-page"
                          >
                            <td className="px-4">
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/mandates/${m.refNo}`)}
                                className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
                              >
                                {m.refNo}
                              </button>
                            </td>
                            <td className="px-4">
                              <p className="text-sm font-medium text-ink">{m.customerName}</p>
                              <p className="text-xs text-ink-secondary">{m.customerMobile}</p>
                            </td>
                            <td className="px-4 text-right">
                              <p className="text-sm font-medium text-ink">{formatINR(m.totalPayable)}</p>
                              <p className="text-xs text-ink-secondary">{m.tenureMonths} months</p>
                            </td>
                            <td className="px-4">
                              <StatusBadge status={m.status} />
                            </td>
                            <td className="px-4">
                              <p className="text-[13px] text-ink">{formatDateMaybe(m.createdAt)}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <EmptyState
                icon={FileText}
                title="No mandates yet"
                description="This retailer has not created any mandates so far."
              />
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-ink">KYC</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/kyc/${retailer.id}`)}>
                  View Application
                </Button>
              </div>
              {!kyc || !kyc.submittedAt ? (
                <p className="mt-3 text-[13px] text-ink-secondary">No KYC application found.</p>
              ) : (
                <>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-[13px] text-ink-secondary">Status</span>
                    <KycStatusBadge status={kyc.status as KycStatus} />
                  </div>
                  <div className="mt-3 flex flex-col divide-y divide-[#F1F5F9]">
                    <div className="flex items-center justify-between gap-4 py-2.5">
                      <span className="text-[13px] text-ink-secondary">Business Type</span>
                      <span className="text-sm font-medium text-ink">{kyc.businessType ?? "—"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2.5">
                      <span className="text-[13px] text-ink-secondary">Business PAN</span>
                      <span className="font-mono text-sm font-medium text-ink">{kyc.panNumber || "—"}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-2.5">
                      <span className="text-[13px] text-ink-secondary">Submitted On</span>
                      <span className="text-sm font-medium text-ink">{kyc.submittedAt || "—"}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-ink">Wallet</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/wallets/${retailer.id}`)}>
                  View Ledger
                </Button>
              </div>
              <div className="mt-2 flex flex-col divide-y divide-[#F1F5F9]">
                <div className="flex items-center justify-between gap-4 py-3">
                  <span className="text-[13px] text-ink-secondary">Available Balance</span>
                  <span className="text-xl font-semibold text-ink">{formatINR(wallet?.totalBalance ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-[13px] text-ink-secondary">Lifetime Spent</span>
                  <span className="text-sm font-medium text-ink">{formatINR(wallet?.lifetimeSpent ?? 0)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <span className="text-[13px] text-ink-secondary">Last Recharge</span>
                  <span className="text-sm font-medium text-ink">
                    {wallet?.lastRechargeAt ? formatDate(wallet.lastRechargeAt) : "—"}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Account Details</h3>
              <dl className="mt-3 flex flex-col">
                {accountRows.map((row, i) => (
                  <div
                    key={row.label}
                    className={`flex items-center justify-between gap-4 py-2.5 ${
                      i > 0 ? "border-t border-[#F1F5F9]" : ""
                    }`}
                  >
                    <dt className="shrink-0 text-[13px] text-ink-secondary">{row.label}</dt>
                    <dd className="min-w-0 truncate text-right text-sm font-medium text-ink">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {hasMandates && (
              <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
                <h3 className="text-lg font-semibold text-ink">Status Breakdown</h3>
                <div className="mt-5 flex justify-center">
                  <DonutChart
                    segments={segments}
                    size={160}
                    strokeWidth={28}
                    centerValue={`${stats.totalMandates}`}
                    centerLabel="Total"
                  />
                </div>
                <div className="mt-5 flex flex-col gap-2.5">
                  {segments.map((s) => (
                    <div key={s.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-[13px] text-ink">{STATUS_META[s.label].label}</span>
                      </div>
                      <span className="text-[13px] font-medium text-ink-secondary">
                        {s.value} · {stats.totalMandates > 0 ? Math.round((s.value / stats.totalMandates) * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
              <h3 className="text-lg font-semibold text-ink">Admin Notes</h3>
              <div className="mt-4">
                <Textarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add an internal note about this retailer..."
                />
                <div className="mt-3 flex justify-end">
                  <Button onClick={saveNote}>Save Note</Button>
                </div>
              </div>
              {notes.length > 0 ? (
                <div className="mt-5 flex flex-col">
                  {notes.map((n, i) => (
                    <div key={i} className={`${i > 0 ? "border-t border-[#F1F5F9] pt-4" : ""} ${i < notes.length - 1 ? "pb-4" : ""}`}>
                      <p className="text-[13px] text-[#0F172A]">{n.text}</p>
                      <p className="mt-1 text-xs text-[#94A3B8]">
                        {n.by} · {n.ts}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-[13px] text-ink-muted">No notes added yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <RetailerFormModal open={editOpen} onClose={() => setEditOpen(false)} retailer={retailer} onSave={handleSave} />
      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        retailer={retailer}
        onReset={handleResetPassword}
      />
      <DeactivateModal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        retailer={retailer}
        onConfirm={handleDeactivate}
      />
    </div>
  );
}

function formatDateMaybe(value: string): string {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return formatDate(d.toISOString());
}

interface MenuActionProps {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

function MenuAction({ icon, children, onClick, danger }: MenuActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50 cursor-pointer ${
        danger ? "text-danger" : "text-ink"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}
